import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Building2, RefreshCw, Search, ChevronLeft, ChevronRight, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const EmpresasColaboradoras = () => {
  const { token, isAdmin } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [users, setUsers] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, last_sync: null });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filterEmpresa, setFilterEmpresa] = useState('');
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const LIMIT = 15;

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    axios.get(`${API}/empresas-colaboradoras/stats`, { headers }).then(r => setStats(r.data)).catch(() => {});
  }, [token]);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filterEmpresa) params.set('empresa_id', filterEmpresa);
      const res = await axios.get(`${API}/empresas-colaboradoras/usuarios-tabla?${params}`, { headers });
      setUsers(res.data.data);
      setTotal(res.data.total);
      if (empresas.length === 0 && res.data.empresas) setEmpresas(res.data.empresas);
    } catch { toast.error('Error al cargar usuarios'); }
    setLoading(false);
  }, [token, page, debouncedSearch, filterEmpresa]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      await axios.post(`${API}/empresas-colaboradoras/sync`, {}, { headers });
      toast.success('Sincronizacion completada');
      fetchUsers();
      axios.get(`${API}/empresas-colaboradoras/stats`, { headers }).then(r => setStats(r.data));
    } catch { toast.error('Error al sincronizar'); }
    setSyncing(false);
  };

  const totalPages = Math.ceil(total / LIMIT);

  const formatDate = (d) => {
    if (!d) return '';
    try {
      const date = new Date(d);
      return `${String(date.getDate()).padStart(2, '0')}-${String(date.getMonth() + 1).padStart(2, '0')}-${date.getFullYear()}`;
    } catch { return d; }
  };

  return (
    <div className="space-y-4" data-testid="empresas-colaboradoras-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-900" data-testid="page-title">Empresas Colaboradoras</h1>
          <p className="text-sm text-slate-500">
            {stats.total} empresas | {total} trabajadores
            {stats.last_sync && ` | Sync: ${formatDate(stats.last_sync)}`}
          </p>
        </div>
        {isAdmin() && (
          <button onClick={handleSync} disabled={syncing}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-teal-600 text-white text-sm font-medium rounded-lg hover:bg-teal-700 disabled:opacity-50 transition-colors"
            data-testid="sync-button">
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Sincronizando...' : 'Sincronizar'}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por nombre, RUT o email..."
            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/20"
            data-testid="search-input" />
          {search && (
            <button onClick={() => setSearch('')} className="absolute right-3 top-2.5">
              <X className="w-4 h-4 text-slate-400 hover:text-slate-600" />
            </button>
          )}
        </div>
        <select value={filterEmpresa} onChange={e => { setFilterEmpresa(e.target.value); setPage(1); }}
          className="px-3 py-2 border border-slate-200 rounded-lg text-sm" data-testid="filter-empresa">
          <option value="">Todas las empresas ({stats.total})</option>
          {empresas.map(e => <option key={e.id} value={e.id}>{e.razon_social}</option>)}
        </select>
        <span className="text-xs text-slate-400">{total} trabajador(es)</span>
      </div>

      {/* Table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="empresas-users-table">
            <thead className="bg-slate-50">
              <tr className="border-b border-slate-200">
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase whitespace-nowrap">RUT</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase">Nombre</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase">Empresa</th>
                <th className="text-left px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase">Email</th>
                <th className="text-center px-3 py-2.5 text-[10px] font-semibold text-slate-400 uppercase">Clave</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={5} className="px-3 py-8 text-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-teal-500 mx-auto" />
                </td></tr>
              ) : users.length === 0 ? (
                <tr><td colSpan={5} className="px-3 py-8 text-center text-sm text-slate-400">
                  No se encontraron trabajadores
                </td></tr>
              ) : users.map(u => (
                <tr key={u.id} className="hover:bg-slate-50/50" data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-2 text-xs font-mono text-slate-600 whitespace-nowrap">{u.rut}</td>
                  <td className="px-3 py-2 text-xs font-medium text-slate-800">{u.nombre}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{u.empresa}</td>
                  <td className="px-3 py-2 text-xs text-slate-500">{u.email || '-'}</td>
                  <td className="px-3 py-2 text-center">
                    <span className={`text-xs font-mono ${u.clave ? 'text-slate-700' : 'text-slate-400'}`}>
                      {u.clave || '-'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between" data-testid="pagination">
          <span className="text-xs text-slate-400">{(page - 1) * LIMIT + 1}-{Math.min(page * LIMIT, total)} de {total}</span>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-30">
              <ChevronLeft className="w-3.5 h-3.5" /> Anterior
            </button>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let p;
              if (totalPages <= 7) p = i + 1;
              else if (page <= 4) p = i + 1;
              else if (page >= totalPages - 3) p = totalPages - 6 + i;
              else p = page - 3 + i;
              return (
                <button key={p} onClick={() => setPage(p)}
                  className={`w-8 h-8 text-xs rounded-lg ${p === page ? 'bg-teal-600 text-white' : 'border border-slate-200 hover:bg-slate-50 text-slate-600'}`}>
                  {p}
                </button>
              );
            })}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-30">
              Siguiente <ChevronRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmpresasColaboradoras;
